import * as React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
    FeaturedProperties,
    internalPromoIds
} from '@zoopla/listing-search/featured/properties';
import { SeoBlurb } from '@zoopla/listing-search-context';
import { getOnClickTracking } from './featuredPropertyTracking';

export type InternalPromoId = typeof internalPromoIds[number];

type Product =
    | {
          listingId: string;
          featuredType?: string | null;
          title?: string | null;
          price?: string | null;
          alternativeRentFrequencyLabel?: string | null;
          address?: string | null;
          branch?: { logoUrl?: string | null; name?: string | null } | null;
          listingUris?: {
              detail?: string | null;
          } | null;
          features?:
              | ({
                    iconId?: string | null;
                    content?: number | null;
                } | null)[]
              | null;
          image?: {
              src?: string | null;
              responsiveImgList?:
                  | ({ src?: string | null; width?: number | null } | null)[]
                  | null;
          } | null;
      }
    | InternalPromoId;

export type PromoIds = typeof internalPromoIds[number];

type FeaturedItem = (
    | { type: 'Internal Promo'; id: PromoIds }
    | { type: 'Featured Property'; data: Product }
) & { onClick: () => void };

const getFeaturedItems = (items: (Product | PromoIds)[]): FeaturedItem[] =>
    items.map((item, index) =>
        item === 'newhomes' || item === 'mobile' || item === 'smartmap'
            ? {
                  type: 'Internal Promo',
                  id: item,
                  onClick: getOnClickTracking(item, index)
              }
            : {
                  type: 'Featured Property',
                  data: item,
                  onClick: getOnClickTracking(item, index)
              }
    );

type Props = {
    readonly properties: (Product | PromoIds)[];
    sectionType: SeoBlurb['transactionType'];
    propertyType?: string;
    // the area identifier of current results, e.g. "london" or "london/copper-row/se1-2lh"
    identifier?: string;
    searchParam?: string;
    searchIdentifier: string;
};

const StyledFeaturedListings = styled(FeaturedProperties)(
    ({ theme }) => css`
        ${theme.mediaQueries.medium} {
            margin-bottom: ${theme.spacing['24px']};
        }

        ${theme.mediaQueries.extraLarge} {
            margin-bottom: ${theme.spacing['32px']};
        }
    `
);

function FeaturedPropertyList({
    properties,
    sectionType,
    identifier,
    searchParam,
    propertyType,
    searchIdentifier
}: Props) {
    return (
        <StyledFeaturedListings
            featuredItems={getFeaturedItems(properties)}
            sectionType={sectionType}
            searchParam={
                identifier
                    ? `${
                          propertyType ? propertyType + '/' : 'property/'
                      }${identifier}/?identifier=${identifier}${
                          searchParam ? `&${searchParam}` : ''
                      }`
                    : ''
            }
            searchIdentifier={searchIdentifier}
        />
    );
}

export default FeaturedPropertyList;
